import { Button, Header, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAddressBook } from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { logger } from 'src/analytics/KatalLogger';
import { getContactDetails } from 'src/api/app-sync-services';
import { LoadingSpinner } from 'src/components/common/LoadingSpinner';
import XptWidgetTable from 'src/components/common/xpt-table/XptWidgetTable';
import { ContactInformation } from 'src/models/AppContextModels';
import { WidgetContainer } from '../WidgetContainer';
import { Contacts_Header_Config } from './ContactUtils';
import { useAuth } from 'src/app/auth/AuthContextProvider';
import ContactsForm from './ContactsForm';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import { compareNullableNumbers } from 'src/utils/generic-utilities';

interface ContactsWidgetProps {
  dataClassificationId?: number;
}

const ContactsWidget: React.FC<ContactsWidgetProps> = ({ dataClassificationId }) => {
  const { isDev } = useAuth();
  const { userAccessForCurrentBusinessGroup } = useSelector((state: RootState) => state.xptAccessAndAuthorizationStore);
  const businessGroups = useSelector((state: RootState) => state.businessGroupStore.businessGroups);
  const isFinanceAdminOrFinanceOwner = userAccessForCurrentBusinessGroup?.isFinanceAdminOrFinanceOwner || false;

  const [contacts, setContacts] = useState<ContactInformation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);

  useEffect(() => {
    loadContacts();
  }, [dataClassificationId]);

  const loadContacts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const fetchedContacts = await getContactDetails();
      setContacts(fetchedContacts);
    } catch (err: any) {
      setContacts([]);
      setError('Failed to load resources');
      logger.error('Failed to load contacts widget data', err);
    } finally {
      setIsLoading(false);
    }
  };

  const onCancelCreateContact = () => {
    setShowModal(false);
  };

  const onConfirmingCreateContact = () => {
    setShowModal(false);
    loadContacts();
  };

  // Sort contacts by display_order_id from business groups and create business group name map
  const { sortedContacts, businessGroupsMap } = useMemo(() => {
    // Create a map of data_classification_id to display_order_id for efficient lookup
    const displayOrderMap = new Map<number, number | null>();
    const businessGroupsMap = new Map<number, string>();

    businessGroups.forEach((bg) => {
      if (bg.data_classification.data_classification_id !== null) {
        displayOrderMap.set(bg.data_classification.data_classification_id, bg.display_order_id ?? null);
        businessGroupsMap.set(bg.data_classification.data_classification_id, bg.data_classification.data_classification_name);
      }
    });

    // Sort contacts based on display_order_id
    const sorted = [...contacts].sort((a, b) => {
      const displayOrderA =
        a.data_classification_id !== null && a.data_classification_id !== undefined ? displayOrderMap.get(a.data_classification_id) ?? null : null;
      const displayOrderB =
        b.data_classification_id !== null && b.data_classification_id !== undefined ? displayOrderMap.get(b.data_classification_id) ?? null : null;

      return compareNullableNumbers(displayOrderA, displayOrderB, 'asc');
    });

    return { sortedContacts: sorted, businessGroupsMap };
  }, [contacts, businessGroups]);

  const widgetHeader = (
    <Header
      variant="h3"
      actions={
        <SpaceBetween size="s" direction="horizontal">
          {(isDev || isFinanceAdminOrFinanceOwner) && <Button onClick={() => setShowModal(true)}>Edit</Button>}
        </SpaceBetween>
      }
    >
      <SpaceBetween size="xs" direction="horizontal">
        <FontAwesomeIcon icon={faAddressBook} />
        <span>Key Contacts</span>
      </SpaceBetween>
    </Header>
  );

  return (
    <>
      <ContactsForm
        showModal={showModal}
        onCancel={onCancelCreateContact}
        onConfirm={onConfirmingCreateContact}
        contacts={contacts}
        businessGroupsMap={businessGroupsMap}
      />
      <WidgetContainer header={widgetHeader}>
        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <StatusIndicator type="error">{error}</StatusIndicator>
        ) : (
          <>
            <XptWidgetTable
              variant="embedded"
              loadingStatus={false}
              allItems={sortedContacts}
              columnDefinitions={Contacts_Header_Config(businessGroupsMap)}
              hasPagination={false}
              itemsPerPage={5}
            />
          </>
        )}
      </WidgetContainer>
    </>
  );
};

export default ContactsWidget;
